import { AxiosError } from "axios";
import { AUTH_API_KEYS } from "@/features/auth/config/authApiCodes";
import { AUTH_API_ENDPOINTS } from "@/features/auth/config/authApiPath";
import { accessTokenMemory } from "@/features/auth/services/accessTokenMemory";
import {
  findFreshAccessToken, publishAccessToken, publishLogout, waitForFreshAccessToken, withRefreshLock,
} from "@/features/auth/services/authTokenSync";
import { authStorageService } from "@/features/auth/services/storage.service";
import {
  type AuthResponse,
  type RefreshTokenResponse,
  type AuthLoginPayload,
  type AuthErrorResponse,
  type AuthCheck2FAPayload,
  type AuthCheck2FAResponse,
  AuthRegisterPhonePayload,
  AuthRegisterPhoneResponse,
  type AuthResetPasswordPayload,
  type AuthResetPasswordResponse,
  type AuthSetPasswordPayload,
  type AuthSetPasswordResponse,
  type AuthResendResetLinkPayload,
  type AuthResendResetLinkResponse,
  type AuthLoginResponse,
  type ImpersonatePayload,
  type LoginApprovalPending,
  isAuthLoginResponse,
} from "@/features/auth/types/auth.type";
import { fetcher, fetcherWithout401Retry } from "@/shared/lib/fetcher.client";

const GENERIC_ERROR_RESPONSE = {
  error: {
    code: "unknown",
    title: "Erreur",
    description: "Une erreur est survenue",
  },
};

// Réponse pour les erreurs SANS réponse HTTP (timeout, coupure réseau, requête
// bloquée par CORS/proxy — status 0 côté navigateur). Dans ce cas
// error.response est undefined : la résoudre telle quelle faisait remonter
// `undefined` comme un succès de mutation, et useAuthState crashait en lisant
// `error.error` (Sentry YEATI-BENEF, TypeError sur /login).
const NETWORK_ERROR_RESPONSE = {
  error: {
    code: "network_error",
    title: "Connexion impossible",
    description: "Le serveur est momentanément injoignable. Vérifiez votre connexion internet et réessayez.",
  },
};

export const loginUser = async (payload: AuthLoginPayload): Promise<AuthResponse | AuthErrorResponse> => {
  try {
    const response = await fetcher.post<AuthResponse>(AUTH_API_ENDPOINTS.LOGIN, {
      ...payload,
      device_id: authStorageService.getAuthDeviceId(),
      scope: AUTH_API_KEYS.SCOPE,
      grant_type: AUTH_API_KEYS.GRANT_TYPE_PASSWORD,
    });
    return response.data;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return GENERIC_ERROR_RESPONSE;
  }
};

export const check2FA = async (payload: AuthCheck2FAPayload): Promise<AuthCheck2FAResponse | AuthErrorResponse> => {
  try {
    const response = await fetcher.post<AuthCheck2FAResponse>(AUTH_API_ENDPOINTS.TWO_FACTOR_VERIFICATION, {
      ...payload,
      device_id: authStorageService.getAuthDeviceId(),
      scope: AUTH_API_KEYS.SCOPE,
      grant_type: AUTH_API_KEYS.GRANT_TYPE_PASSWORD,
    });
    return response.data;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return {
      error: {
        code: "unknown",
        title: "Erreur",
        description: "Une erreur est survenue",
      },
    };
  }
};

// Sign-in approved in the Yeati app (yeati-api, docs/development/app-login-approval.md). Without
// the 401 retry: a 401 here is the end of the request (denied, expired), not a token to refresh.
export const pollLoginApproval = async (
  payload: { challenge: string, pollSecret: string },
): Promise<LoginApprovalPending | AuthCheck2FAResponse | AuthErrorResponse> => {
  try {
    const response = await fetcherWithout401Retry.post<LoginApprovalPending | AuthCheck2FAResponse>(AUTH_API_ENDPOINTS.LOGIN_APPROVAL_STATUS, {
      challenge: payload.challenge,
      poll_secret: payload.pollSecret,
      device_id: authStorageService.getAuthDeviceId(),
    });
    return response.data;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return GENERIC_ERROR_RESPONSE;
  }
};

// « Recevoir un code par SMS »: the request is cancelled, an SMS code follows (verify_2fa_token).
export const fallbackLoginApprovalToSms = async (
  payload: { challenge: string, pollSecret: string },
): Promise<AuthResponse | AuthErrorResponse> => {
  try {
    const response = await fetcherWithout401Retry.post<AuthResponse>(AUTH_API_ENDPOINTS.RESEND_TWO_FACTOR, {
      challenge_id: payload.challenge,
      poll_secret: payload.pollSecret,
      device_id: authStorageService.getAuthDeviceId(),
      channel: "sms",
    });
    return response.data;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return GENERIC_ERROR_RESPONSE;
  }
};

export const registerPhone = async (payload: AuthRegisterPhonePayload): Promise<AuthRegisterPhoneResponse | AuthErrorResponse> => {
  try {
    const response = await fetcher.post<AuthRegisterPhoneResponse>(AUTH_API_ENDPOINTS.REGISTER_PHONE, {
      ...payload,
      device_id: authStorageService.getAuthDeviceId(),
      scope: AUTH_API_KEYS.SCOPE,
      grant_type: AUTH_API_KEYS.GRANT_TYPE_PASSWORD,
    });
    return response.data;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return GENERIC_ERROR_RESPONSE;
  }
};

export const refreshToken = async (): Promise<RefreshTokenResponse | AuthErrorResponse> => {
  try {
    if (import.meta.env.LOCAL_BROWSER_LOGIN) {
      const { consumeLocalSession } = await import("@/features/auth/local-browser-login/session");
      const localSession = consumeLocalSession();
      if (localSession) {
        authStorageService.saveAuthToken(localSession.access_token, localSession.expires_in);
        return localSession;
      }
    }

    // Null on a page load: the token then comes from the refresh cookie alone.
    const staleToken = authStorageService.getAuthToken();

    const rotate = async (contended: boolean): Promise<RefreshTokenResponse> => {
      // Another refresh — of this tab or of another one — ended while this one waited for the lock.
      const fresh = contended ? await waitForFreshAccessToken(staleToken) : findFreshAccessToken(staleToken);
      if (fresh) {
        accessTokenMemory.setAccessToken(fresh.accessToken, fresh.expiresAt);
        const expiresIn = fresh.expiresAt === null ? 0 : Math.floor((fresh.expiresAt - Date.now()) / 1000);
        return { access_token: fresh.accessToken, token_type: "Bearer", expires_in: expiresIn };
      }

      const response = await fetcherWithout401Retry.post<RefreshTokenResponse>(
        AUTH_API_ENDPOINTS.REFRESH_TOKEN,
        {
          device_id: authStorageService.getAuthDeviceId(),
          scope: AUTH_API_KEYS.SCOPE,
          grant_type: AUTH_API_KEYS.GRANT_TYPE_REFRESH_TOKEN,
        },
        // The httponly cookie names the session. A held token is not sent: the API would
        // refuse the refresh if this tab missed the token another tab rotated to.
        { headers: { Authorization: false } },
      );
      // Saved and published before releasing the lock, so that waiting tabs reuse the token.
      if (isAuthLoginResponse(response.data)) {
        authStorageService.saveAuthToken(response.data.access_token, response.data.expires_in);
        publishAccessToken(response.data.access_token, accessTokenMemory.getExpiresAt());
      }
      return response.data;
    };

    return await withRefreshLock(rotate);
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return GENERIC_ERROR_RESPONSE;
  }
};

export const resetPassword = async (payload: AuthResetPasswordPayload): Promise<AuthResetPasswordResponse | AuthErrorResponse> => {
  try {
    const response = await fetcher.post<AuthResetPasswordResponse>(AUTH_API_ENDPOINTS.RESET_PASSWORD, {
      ...payload,
      scope: AUTH_API_KEYS.SCOPE, // Add scope to target the correct user type
    });
    return response.data;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return GENERIC_ERROR_RESPONSE;
  }
};

export const setPassword = async (payload: AuthSetPasswordPayload): Promise<AuthSetPasswordResponse | AuthErrorResponse> => {
  try {
    const response = await fetcher.post<AuthSetPasswordResponse>(AUTH_API_ENDPOINTS.SET_PASSWORD, payload);
    return response.data;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return GENERIC_ERROR_RESPONSE;
  }
};

export const resendResetLink = async (payload: AuthResendResetLinkPayload): Promise<AuthResendResetLinkResponse | AuthErrorResponse> => {
  try {
    const response = await fetcher.post<AuthResendResetLinkResponse>(AUTH_API_ENDPOINTS.RESEND_RESET_LINK, payload);
    return response.data;
  }
  catch (error: unknown) {
    if (error instanceof AxiosError) {
      return error.response?.data ?? NETWORK_ERROR_RESPONSE;
    }
    return GENERIC_ERROR_RESPONSE;
  }
};

export const logoutUser = async (token?: string): Promise<void> => {
  const authToken = token ?? authStorageService.getAuthToken();

  if (authToken) {
    // Logout is immediate on purpose: this call only revokes the token server-side. It rejects
    // (e.g. 403) when the token already expired, which is nothing worth reporting.
    fetcherWithout401Retry.post<void>(AUTH_API_ENDPOINTS.LOGOUT, {
      device_id: authStorageService.getAuthDeviceId(),
      scope: AUTH_API_KEYS.SCOPE,
    },
    { headers: { Authorization: `Bearer ${authToken}` } },
    ).catch(() => {});
    // The other tabs share the session the API is revoking: they log out too.
    publishLogout();
  }

  authStorageService.deleteAuthToken();
};

export const exchangeGrantLogin = async (loginToken: string): Promise<AuthLoginResponse> => {
  authStorageService.deleteAuthToken();

  const response = await fetcherWithout401Retry.post<AuthLoginResponse>(
    AUTH_API_ENDPOINTS.EXCHANGE_GRANT_LOGIN,
    {
      login_token: loginToken,
      device_id: authStorageService.getAuthDeviceId(),
    },
  );

  if (response.status !== 200) throw new Error("Grant exchange failed");

  authStorageService.saveAuthToken(response.data.access_token, response.data.expires_in);
  return response.data;
};

export const impersonate = async ({ organization_id, token, employee_id }: ImpersonatePayload): Promise<AuthLoginResponse> => {
  authStorageService.deleteAuthToken(); // Ensure a clean state before impersonation

  const response = await fetcherWithout401Retry.post<AuthLoginResponse>(
    AUTH_API_ENDPOINTS.IMPERSONATE,
    {
      organization_id,
      employee_id,
      device_id: authStorageService.getAuthDeviceId(),
    },
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (response.status !== 200) throw new Error("Impersonate failed");

  authStorageService.saveAuthToken(response.data.access_token, response.data.expires_in);
  return response.data;
};
